import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { Page } from '@/components/Page'
import { formatPKR } from '@/lib/money'
import { openWorkspaceDb } from '@/offline/db'
import { getActiveWorkspaceId } from '@/features/workspace/workspace'

const selCls =
  'rounded-lg border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-slate-200 focus:border-sky-500 focus:outline-none'

/** Stock list with IMEI/model search and variant/status filters (owner request). */
export function StockListPage() {
  const ws = getActiveWorkspaceId()

  const devices = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).devices.where('workspace_id').equals(ws).toArray() : []),
    [ws],
  )
  const purchases = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).purchases.where('workspace_id').equals(ws).toArray() : []),
    [ws],
  )
  const categories = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).categories.where('workspace_id').equals(ws).toArray() : []),
    [ws],
  )
  const imeis = useLiveQuery(
    async () => (ws ? openWorkspaceDb(ws).device_imeis.where('workspace_id').equals(ws).toArray() : []),
    [ws],
  )
  const pending = useLiveQuery(async () => (ws ? openWorkspaceDb(ws).outbox.count() : 0), [ws])

  const [q, setQ] = useState('')
  const [fStatus, setFStatus] = useState<'all' | 'in_stock' | 'sold'>('all')
  const [fCategory, setFCategory] = useState('all')
  const [fBrand, setFBrand] = useState('all')
  const [fPta, setFPta] = useState('all')
  const [fStorage, setFStorage] = useState('all')
  const [fRam, setFRam] = useState('all')

  const imeiByDevice = useMemo(() => {
    const m = new Map<string, string[]>()
    for (const im of imeis ?? []) {
      const arr = m.get(im.device_id) ?? []
      arr.push(im.imei_normalized)
      m.set(im.device_id, arr)
    }
    return m
  }, [imeis])

  const storageOptions = useMemo(
    () => Array.from(new Set((devices ?? []).map((d) => d.storage).filter((s): s is string => !!s))).sort(),
    [devices],
  )
  const ramOptions = useMemo(
    () => Array.from(new Set((devices ?? []).map((d) => d.ram).filter((s): s is string => !!s))).sort(),
    [devices],
  )

  const catName = (id: string) => categories?.find((c) => c.id === id)?.name ?? '—'
  const costOf = (deviceId: string) => purchases?.find((p) => p.device_id === deviceId)?.cost_paisa ?? null

  const needle = q.trim().toLowerCase()
  const digits = q.replace(/\D/g, '')
  const filtered = (devices ?? [])
    .filter((d) => !d.deleted_at)
    .filter((d) => (fStatus === 'all' ? true : d.status === fStatus))
    .filter((d) => (fCategory === 'all' ? true : d.category_id === fCategory))
    .filter((d) => (fBrand === 'all' ? true : d.brand_code === fBrand))
    .filter((d) => (fPta === 'all' ? true : d.pta_status === fPta))
    .filter((d) => (fStorage === 'all' ? true : d.storage === fStorage))
    .filter((d) => (fRam === 'all' ? true : d.ram === fRam))
    .filter((d) => {
      if (!needle) return true
      const imeiList = imeiByDevice.get(d.id) ?? []
      return (
        d.model.toLowerCase().includes(needle) ||
        (d.brand_code ?? '').toLowerCase().includes(needle) ||
        (digits.length > 0 && imeiList.some((im) => im.includes(digits)))
      )
    })

  const totalCost = filtered.reduce((sum, d) => sum + (costOf(d.id) ?? 0), 0)

  return (
    <Page title="Stock" note={`${filtered.length} shown • stock cost ${formatPKR(totalCost)}`}>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs text-slate-400">
          {pending ? `${pending} change(s) pending sync` : 'All changes synced'}
        </span>
        <Link to="/inventory/new" className="rounded-lg bg-sky-500 px-3 py-1.5 text-sm font-semibold text-white">
          + Add purchase
        </Link>
      </div>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search IMEI or model…"
        inputMode="numeric"
        className="mb-2 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:outline-none"
      />

      <div className="mb-1 grid grid-cols-3 gap-2">
        <select className={selCls} value={fStatus} onChange={(e) => setFStatus(e.target.value as typeof fStatus)} aria-label="Status filter">
          <option value="all">Status: all</option>
          <option value="in_stock">In stock</option>
          <option value="sold">Sold</option>
        </select>
        <select className={selCls} value={fCategory} onChange={(e) => setFCategory(e.target.value)} aria-label="Category filter">
          <option value="all">Category: all</option>
          {(categories ?? []).filter((c) => !c.archived_at).map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select className={selCls} value={fBrand} onChange={(e) => setFBrand(e.target.value)} aria-label="Brand filter">
          <option value="all">Brand: all</option>
          <option value="samsung">Samsung</option>
          <option value="oneplus">OnePlus</option>
          <option value="apple">Apple</option>
          <option value="google">Google</option>
        </select>
      </div>
      <div className="mb-3 grid grid-cols-3 gap-2">
        <select className={selCls} value={fPta} onChange={(e) => setFPta(e.target.value)} aria-label="PTA filter">
          <option value="all">PTA: all</option>
          <option value="approved">Approved</option>
          <option value="non_pta">Non PTA</option>
          <option value="unknown">Unknown</option>
        </select>
        <select className={selCls} value={fStorage} onChange={(e) => setFStorage(e.target.value)} aria-label="Storage filter">
          <option value="all">Storage: all</option>
          {storageOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select className={selCls} value={fRam} onChange={(e) => setFRam(e.target.value)} aria-label="RAM filter">
          <option value="all">RAM: all</option>
          {ramOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {filtered.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-700 px-4 py-8 text-center text-sm text-slate-500">
          {needle || fStatus !== 'all' || fCategory !== 'all' || fBrand !== 'all' || fPta !== 'all' || fStorage !== 'all' || fRam !== 'all'
            ? 'No devices match your search/filters.'
            : 'No stock yet. Add your first purchase.'}
        </p>
      ) : (
        <ul className="space-y-2">
          {filtered.map((d) => (
            <li key={d.id} className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-2.5">
              <div className="flex items-center justify-between">
                <Link to={`/inventory/${d.id}`} className="font-medium underline-offset-2 hover:underline">
                  {d.model}
                </Link>
                <span className="text-sm tabular-nums text-slate-300">
                  {costOf(d.id) != null ? formatPKR(costOf(d.id) as number) : '—'}
                </span>
              </div>
              <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-slate-400">
                <span>{catName(d.category_id)}</span>
                {d.storage ? <span>{d.storage}</span> : null}
                {d.ram ? <span>{d.ram}</span> : null}
                {d.status !== 'in_stock' ? <span className="text-slate-500">({d.status})</span> : null}
                {d.pta_status !== 'unknown' ? (
                  <span className={d.pta_status === 'approved' ? 'text-emerald-400' : 'text-amber-400'}>
                    {d.pta_status === 'approved' ? 'PTA' : 'Non PTA'}
                  </span>
                ) : null}
              </div>
              {(imeiByDevice.get(d.id) ?? []).length > 0 ? (
                <p className="mt-1 font-mono text-[11px] text-slate-500">{(imeiByDevice.get(d.id) ?? []).join(' · ')}</p>
              ) : null}
              {d.status === 'in_stock' ? (
                <div className="mt-2 flex justify-end">
                  <Link to={`/inventory/${d.id}/sell`} className="rounded-lg bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-400">
                    Sell
                  </Link>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Page>
  )
}
